import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, ShieldAlert, X } from 'lucide-react';
import {
  EMAIL_REGEX,
  validatePasswordStrength,
  validatePhone,
} from 'shared';
import { useCreateInitialAdmin } from '@/hooks/useAuth';

/**
 * InitialAdminSetupModal — one-shot bootstrap dialog.
 *
 * This modal is only rendered when the API confirms no staff user exists yet
 * (GET /api/auth/setup/status → { needsSetup: true }). The confirm button
 * calls POST /api/auth/setup, which the backend serialises via a Postgres
 * advisory lock inside a serialisable transaction — so even if two operators
 * open the modal at the same time, only one can ever create the first admin.
 *
 * On success the modal closes and the login page's setup-status query is
 * invalidated so it refetches and never shows the modal again.
 */
interface InitialAdminSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
}

export default function InitialAdminSetupModal({
  isOpen,
  onClose,
  onSuccess,
}: InitialAdminSetupModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const createAdmin = useCreateInitialAdmin();

  // Reset every time the modal is (re)opened. Critical: never leak password
  // state across mounts.
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setTouched({});
      setServerError(null);
      setConfirming(false);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const passwordStrength = useMemo(() => validatePasswordStrength(password), [password]);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const errors: FieldErrors = useMemo(() => {
    const e: FieldErrors = {};
    if (!name.trim()) e.name = 'Nome é obrigatório';
    if (!email.trim()) e.email = 'Email é obrigatório';
    else if (!EMAIL_REGEX.test(email.trim())) e.email = 'Formato de email inválido';
    const phoneRes = validatePhone(phone);
    if (!phoneRes.valid) e.phone = phoneRes.errors[0];
    if (!password) e.password = 'Password é obrigatória';
    else if (!passwordStrength.valid) {
      const parts: string[] = [];
      if (passwordStrength.tooShort) parts.push('mín. 8 caracteres');
      if (passwordStrength.missingUpper) parts.push('uma maiúscula');
      if (passwordStrength.missingLower) parts.push('uma minúscula');
      if (passwordStrength.missingNumber) parts.push('um número');
      e.password = `Password fraca: falta ${parts.join(', ')}`;
    }
    if (!confirmPassword) e.confirmPassword = 'Confirma a password';
    else if (password !== confirmPassword) e.confirmPassword = 'As passwords não coincidem';
    return e;
  }, [name, email, phone, password, confirmPassword, passwordStrength]);

  const canSubmit = Object.keys(errors).length === 0 && !createAdmin.isPending;

  if (!isOpen) return null;

  const submit = () => {
    setTouched({ name: true, email: true, phone: true, password: true, confirmPassword: true });
    if (!canSubmit) return;
    setServerError(null);
    createAdmin.mutate(
      {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
        onError: (err: Error) => {
          setServerError(err.message || 'Falha ao criar o administrador inicial.');
          setConfirming(false);
        },
      }
    );
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // We intentionally do NOT close on backdrop click: this is a critical
    // one-shot action; require the operator to click "Voltar ao login".
    e.stopPropagation();
  };

  const showError = (field: keyof FieldErrors) => touched[field] && errors[field];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="initial-setup-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-primary" />
            <h2 id="initial-setup-title" className="text-lg font-semibold text-foreground">
              Criar Administrador Inicial
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Explanatory notice — required by spec */}
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
            role="note"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Esta funcionalidade é utilizada apenas no momento inicial de instalação.
              Depois de criado o primeiro utilizador administrador, este ecrã deixará
              de estar disponível.
            </p>
          </div>

          {serverError && (
            <div
              className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-2.5 text-sm text-red-800 dark:text-red-300"
              role="alert"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              {serverError}
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="setup-name" className="label">
              Nome do administrador
            </label>
            <input
              ref={nameRef}
              id="setup-name"
              type="text"
              className="input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              autoComplete="name"
              maxLength={255}
              aria-invalid={!!showError('name') || undefined}
            />
            {showError('name') && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="setup-email" className="label">
              Email
            </label>
            <input
              id="setup-email"
              type="email"
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              autoComplete="email"
              aria-invalid={!!showError('email') || undefined}
            />
            {showError('email') && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="setup-phone" className="label">
              Telefone
            </label>
            <input
              id="setup-phone"
              type="tel"
              className="input mt-1"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              autoComplete="tel"
              placeholder="+351 912 345 678"
              aria-invalid={!!showError('phone') || undefined}
            />
            {showError('phone') && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.phone}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="setup-password" className="label">
              Password
            </label>
            <div className="relative mt-1">
              <input
                id="setup-password"
                type={showPassword ? 'text' : 'password'}
                className="input pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                autoComplete="new-password"
                aria-invalid={!!showError('password') || undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Ocultar password' : 'Mostrar password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* Strength checklist */}
            <ul className="mt-2 space-y-0.5 text-xs">
              <StrengthItem ok={!passwordStrength.tooShort} label="Mínimo de 8 caracteres" />
              <StrengthItem ok={!passwordStrength.missingUpper} label="Pelo menos uma maiúscula" />
              <StrengthItem ok={!passwordStrength.missingLower} label="Pelo menos uma minúscula" />
              <StrengthItem ok={!passwordStrength.missingNumber} label="Pelo menos um número" />
            </ul>
          </div>

          {/* Confirm password */}
          <div>
            <label htmlFor="setup-password-confirm" className="label">
              Confirmar password
            </label>
            <input
              id="setup-password-confirm"
              type={showPassword ? 'text' : 'password'}
              className="input mt-1"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
              autoComplete="new-password"
              aria-invalid={!!showError('confirmPassword') || undefined}
            />
            {showError('confirmPassword') && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.confirmPassword}
              </p>
            )}
            {passwordsMatch && !errors.confirmPassword && (
              <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> As passwords coincidem
              </p>
            )}
          </div>

          {/* Confirmation step */}
          {confirming && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-900 dark:text-red-200">
              <p className="font-semibold mb-1">Tens a certeza?</p>
              <p>
                Esta ação só pode ser executada uma vez. Depois de criado o
                administrador, este ecrã não voltará a estar disponível.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button type="button" onClick={onClose} className="btn-outline text-sm">
            Voltar ao login
          </button>
          {!confirming ? (
            <button
              type="button"
              onClick={() => {
                setTouched({ name: true, email: true, phone: true, password: true, confirmPassword: true });
                if (canSubmit) setConfirming(true);
              }}
              disabled={!canSubmit}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirmar
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit || createAdmin.isPending}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createAdmin.isPending ? 'A criar…' : 'Sim, criar administrador'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StrengthItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li
      className={`flex items-center gap-1.5 ${
        ok ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 inline-block rounded-full border border-current" />}
      <span>{label}</span>
    </li>
  );
}
